import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import axiosInstance from '../axiosInstance';
import * as general from '../libs/general';
import UserLayout from '../components/UserLayout';
import DataTable from '../components/ui/DataTable';
import Paging from '../components/Paging';
import { canViewChildFeature, TTH_COLUMNS } from '../libs/childUserPermission';

/**
 * Portal khách hàng — Đơn TTH, CHỈ XEM (chưa cho tạo đơn ở giai đoạn này). Gọi
 * /api/tth-orders/searchMyTthOrders — trả về TthOrderCustomerDto, KHÔNG có Mã Đối tác/Tỷ giá ĐT/
 * VNĐ ĐT/Chênh lệch/Trạng thái đối soát — đúng yêu cầu "user không được biết tỷ giá,
 * giao dịch của ZTO với đối tác, chỉ biết được tỷ giá của đối tác với khách hàng thôi".
 *
 * Layout/CSS + cơ chế lib-filter (icon filter trong <th>, đọc window.initializeFilterValue /
 * general.getFormData / getSession) mirror y hệt màn "Đơn hàng" của khách (user/OrderTable.jsx) và
 * màn admin (admin/TthOrderAdmin.jsx): cùng khung table-responsive/web-table, cùng <DataTable
 * className="table-normal">, cùng <table table-striped table-bordered border-normal> + <thead
 * table-primary>. Ô "Trạng thái" tô màu theo colorCode (MasterData.color) như màn admin.
 */
const SESSION_KEY = 'search_my_tth_orders';
const FILTER_FORM_ID = 'tth_order_filter';
const TTH_STATUS_OPTIONS = 'Đơn mới,Khách CK,Thành công,Đơn hủy';

/**
 * Màu nền ô "Trạng thái". Ưu tiên colorCode server trả về (MasterData.color, giống màn admin);
 * fallback theo tên trạng thái để ô vẫn có màu kể cả khi DB chưa cấu hình color cho trạng thái TTH.
 */
const TTH_STATUS_FALLBACK_COLOR = {
  'Đơn mới': '#8feef0',
  'Khách CK': '#f5e7b2',
  'Thành công': '#c1f2a6',
  'Đơn hủy': '#f8c9c9',
};
const resolveStatusColor = (item) => item?.colorCode || TTH_STATUS_FALLBACK_COLOR[item?.statusName] || '';

function TthOrderTable() {
  const config = general.getLocalStorage("user_config");
  const showColQuantity = canViewChildFeature(config, TTH_COLUMNS.QUANTITY);
  const showColRate = canViewChildFeature(config, TTH_COLUMNS.RATE);
  const showColAmount = canViewChildFeature(config, TTH_COLUMNS.AMOUNT);
  const showColStatus = canViewChildFeature(config, TTH_COLUMNS.STATUS);
  const showColNote = canViewChildFeature(config, TTH_COLUMNS.NOTE);
  const [pageData, setPageData] = useState({ content: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [params, setParams] = useState({});
  const hasRun = useRef(false);

  const search = async (filter) => {
    if (window.callingMyTthOrders) return;
    window.callingMyTthOrders = true;
    setIsLoading(true);
    setErrorMessage('');

    let nextParams = general.getSession(SESSION_KEY);
    let formData = general.getFormData('#' + FILTER_FORM_ID);
    if (!hasRun.current) {
      hasRun.current = true;
      formData = general.getSession(SESSION_KEY);
      setSearchQuery(formData.keyword || '');
    }
    Object.assign(nextParams, formData);
    if (filter) Object.assign(nextParams, filter);

    general.setSession(SESSION_KEY, nextParams);
    setParams(nextParams);
    if (window.initializeFilterValue) window.initializeFilterValue(nextParams);

    const filteredParams = Object.fromEntries(
      Object.entries(nextParams).filter(
        ([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size50'
      )
    );

    try {
      const res = await axiosInstance.get('/api/tth-orders/searchMyTthOrders', { params: filteredParams });
      if (res.data?.success === false) {
        setErrorMessage(res.data.message || 'Không tải được dữ liệu');
        setPageData({ content: [] });
      } else {
        setPageData(res.data);
      }
    } catch (err) {
      setErrorMessage(err.message);
      setPageData({ content: [] });
    } finally {
      window.callingMyTthOrders = false;
      setIsLoading(false);
    }
  };

  useEffect(() => { search({ page: 0 }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    search({ page: 0, keyword: e.target.value });
  };
  const handlePageChange = (p) => search({ page: p - 1 });
  const handleItemsPerPageChange = (e) => search({ page: 0, size: parseInt(e.target.value, 10) });

  const content = pageData.page?.content || pageData.content || [];

  // Nguồn gợi ý cho filter-ib cột "Mã đơn" — chỉ mã đơn của chính khách (từ kết quả đã tải),
  // KHÔNG gọi endpoint trả toàn bộ mã đơn để tránh lộ đơn của khách khác.
  const codeOptions = useMemo(
    () => Array.from(new Set(content.map((x) => x.orderCode).filter(Boolean))).join(','),
    [content]
  );

  return (
    <div>
      <UserLayout>
        <div className="table-responsive web-table" data-loading={isLoading}>
          <div className="container-fluid">
            <div className="row bg-zto">
              <div className="col-12 col-md-2 pt-1 pb-1 fw-bold text-white text-uppercase d-flex align-items-center">
                Đơn TTH
              </div>
              <div className="col-5 col-md-3 pt-1 pb-1">
                <Form id={FILTER_FORM_ID} onSubmit={(e) => e.preventDefault()}>
                  <div className="input-group search-block">
                    <input
                      name="keyword"
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Tìm kiếm..."
                      autoComplete="off"
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <button className="btn btn-light btn-sm" type="button" title="Tìm kiếm" onClick={() => search({ page: 0 })}>
                      <FaSearch />
                    </button>
                  </div>
                </Form>
              </div>
              <div className="col-7 col-md-7" />
            </div>

            <div className="row row-control">
              <div className="col-md-12">
                <span className="filter-des hidden-close-i2" />
              </div>
            </div>
          </div>

          <div>
            <section>
              <DataTable maxHeight="calc(100vh - 206px)" className="table-normal" data-loading={isLoading}>
                <table className="table table-striped table-bordered border-normal">
                  <thead className="table-primary">
                    <tr>
                      <th className="sticky text-center" width="50px">ID</th>
                      <th className="text-center">Ngày</th>
                      <th className="text-center">
                        <span>Mã đơn</span>
                        <span className="filter-ib" data-form={FILTER_FORM_ID} data-filter="orderCodes"
                          data-source-key="list_my_tth_codes" data-min="140px"><i className="filter-i" /></span>
                      </th>
                      {showColQuantity && <th className="text-center">Số lượng Tệ</th>}
                      {showColRate && <th className="text-center">Tỷ giá</th>}
                      {showColAmount && <th className="text-center">Thành tiền VNĐ</th>}
                      {showColStatus && (
                        <th className="text-center">
                          <span>Trạng thái</span>
                          <span className="filter-ib" data-form={FILTER_FORM_ID} data-filter="status"
                            data-source-key="list_tth_status" data-min="120px"><i className="filter-i" /></span>
                        </th>
                      )}
                      {showColNote && <th className="text-center">Ghi chú</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {content.map((item) => (
                      <tr key={item.id}>
                        <td className="sticky text-center">{item.id}</td>
                        <td className="text-center" title={general.formatTimeShort(item.createdDate)}>{general.formatDate(item.createdDate)}</td>
                        <td className="text-center">{item.orderCode}</td>
                        {showColQuantity && <td className="text-center">{general.formatDecimal(item.quantity)} {item.currency}</td>}
                        {showColRate && <td className="text-center">{general.formatNumber(item.customerRate)}</td>}
                        {showColAmount && <td className="text-center">{general.formatNumber(item.amountCustomer)}</td>}
                        {showColStatus && (
                          <td
                            className="tth-status-cell"
                            style={{ '--bs-table-bg-type': resolveStatusColor(item), backgroundColor: resolveStatusColor(item) }}
                          >
                            {item.statusName}
                          </td>
                        )}
                        {showColNote && <td className="text-start">{item.note}</td>}
                      </tr>
                    ))}
                    {!isLoading && errorMessage === '' && content.length === 0 && (
                      <tr><td colSpan="8" className="text-center">Bạn chưa có đơn TTH nào</td></tr>
                    )}
                    {errorMessage !== '' && (
                      <tr><td colSpan="8" className="text-center">{errorMessage}</td></tr>
                    )}
                  </tbody>
                </table>
              </DataTable>
            </section>

            <div className="my-2 justify-content-end align-items-center">
              <Paging
                pageData={pageData}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                isShowTotal={true}
              />
            </div>
          </div>
        </div>

        {/* Nguồn dữ liệu cho lib-filter (data-source-key) — giống các input hidden ở màn admin. */}
        <input type="hidden" id="list_tth_status" value={TTH_STATUS_OPTIONS} readOnly />
        <input type="hidden" id="list_my_tth_codes" value={codeOptions} readOnly />
      </UserLayout>
    </div>
  );
}

export default TthOrderTable;
